import * as Sentry from '@sentry/react';
import { get } from 'lodash';
import { useState } from 'react';

import { addressFields } from '@features/Address/constants';
import {
  AdditionalAddressPickerValues,
  AddressPageFormValues,
  FormValues,
} from '@features/Address/types';
import { AddressInput } from '@generated/gql/graphql';
import { useUserInformation } from '@hooks/useUserInformation';
import { useLocalSessionAddressContext } from '@providers/basket/LocalSessionAddressProvider/Context';
import { SubmitAddressParams } from '@src/types/address';

import { useLocalSessionAddress, useOldSession } from '../hooks';
import { parseAddressFormInput } from '../parseAddressFormInput';

const compareAddress = (
  previousAddressValues: FormValues,
  addressValues: AddressInput
) =>
  addressFields.every(
    (field) =>
      get(previousAddressValues, field, '') === get(addressValues, field, '')
  );

const compareHomeOwnerStatus = (
  previousValues: AdditionalAddressPickerValues,
  currentValues: AdditionalAddressPickerValues
) =>
  get(previousValues, 'homeOwnerSelection', '') ===
  get(currentValues, 'homeOwnerSelection', '');

const useLocalSession = () => {
  const userInformation = useUserInformation();
  const { setAddress, address } = useLocalSessionAddressContext();
  const [submittedAddress, setSubmittedAddress] =
    useState<Nullable<SubmitAddressParams>>(null);
  const {
    hasOldSession,
    handleRejectOldSession,
    handleRestoreOldSession,
    localBasketId,
  } = useOldSession();

  const { loading, error, getAddress } = useLocalSessionAddress();
  const [isModalOpen, setModalOpen] = useState(false);

  const setModalOpened = (
    previousAddress: AddressPageFormValues,
    address: AddressInput,
    additionalValues: AdditionalAddressPickerValues
  ) => {
    setAddress(previousAddress);
    setModalOpen(true);
    setSubmittedAddress({ address, additionalValues });
  };

  const getPreviousAddress = async (basketId?: Nullable<string>) => {
    if (!basketId) {
      // Should never happen
      Sentry.captureException(
        new Error('useLocalSession: context flaw, missing local basketId')
      );
      return null;
    }

    const { data } = await getAddress({
      variables: {
        basketId,
      },
    });

    return data ? parseAddressFormInput(data) : null;
  };

  const handleModalOnSubmit = async (
    submitAddress: () => void,
    submittedAddress: AddressInput,
    additionalValues: AdditionalAddressPickerValues
  ) => {
    if (userInformation || !hasOldSession) {
      return submitAddress();
    }

    const previousAddressData = await getPreviousAddress(localBasketId);

    if (!previousAddressData) {
      return submitAddress();
    }

    const isAddressEqual = compareAddress(
      previousAddressData.addressPage,
      submittedAddress
    );

    const isHomeOwnerStatusEqual = compareHomeOwnerStatus(
      previousAddressData.addressPage,
      additionalValues
    );

    if (!isAddressEqual || !isHomeOwnerStatusEqual) {
      return submitAddress();
    }

    setModalOpened(previousAddressData, submittedAddress, additionalValues);
  };

  const handleQuoteContinue =
    (onQuoteContinue: (formValues: SubmitAddressParams) => void) => () => {
      handleRejectOldSession();
      submittedAddress && onQuoteContinue(submittedAddress);
    };

  return {
    hasOldSession,
    wrapLocalSessionModal: handleModalOnSubmit,
    handleQuoteContinue,
    handleRestoreOldSession,
    error,
    loading,
    postcode: address?.addressPage.postcode ?? null,
    isModalOpen,
    setModalOpen,
  };
};

export { useLocalSession };
